<script setup>
import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

const activeIndex = ref(0)
let isReady = false
let observer = null
let triggerInstance = null
let resizeHandler = null
let isScrollingByClick = false // флаг: идёт автоскролл
let scrollBlockTimer = null // таймер для разблокировки

const items = [
	{
		title: 'Разрабатываем дизайн',
		image: new URL('@/assets/img/handmade-masks/handmade-masks_img-1.webm', import.meta.url)
			.href,
	},
	{
		title: 'Печатаем на специальной ткани',
		image: new URL('@/assets/img/handmade-masks/handmade-masks_img-2.webm', import.meta.url)
			.href,
	},
	{
		title: 'Прошиваем ткань',
		image: new URL('@/assets/img/handmade-masks/handmade-masks_img-3.webm', import.meta.url)
			.href,
	},
	{
		title: 'Упаковываем и отправляем',
		image: new URL('@/assets/img/handmade-masks/handmade-masks_img-4.webm', import.meta.url)
			.href,
	},
]

// ⚙️ плавный нативный скролл без ScrollToPlugin
function smoothScrollTo(target, duration = 600) {
	const start = window.scrollY || window.pageYOffset
	const change = target - start
	const startTime = performance.now()

	function easeOutQuad(t) {
		return t * (2 - t)
	}

	function animateScroll(currentTime) {
		const elapsed = currentTime - startTime
		const progress = Math.min(elapsed / duration, 1)
		const ease = easeOutQuad(progress)
		window.scrollTo(0, start + change * ease)
		if (progress < 1) {
			requestAnimationFrame(animateScroll)
		}
	}

	requestAnimationFrame(animateScroll)
}

const setActive = (index) => {
	activeIndex.value = index

	if (triggerInstance && window.innerWidth > 991.98) {
		const totalSteps = items.length
		const section = document.querySelector('.handmade-masks')
		const wrapper = section.querySelector('.handmade-masks__wrapper')

		const baseHeight = wrapper.offsetHeight
		const startY = triggerInstance.start
		const endY = triggerInstance.end
		const progress = index / (totalSteps - 1)
		const targetScroll = startY + progress * (endY - startY)

		isScrollingByClick = true

		smoothScrollTo(targetScroll, 600)

		clearTimeout(scrollBlockTimer)
		scrollBlockTimer = setTimeout(() => {
			isScrollingByClick = false
			document.body.style.overflow = '' // разблокируем
		}, 650)
	}
}

function debounce(fn, ms = 150) {
	let t
	return (...args) => {
		clearTimeout(t)
		t = setTimeout(() => fn(...args), ms)
	}
}

async function initAnimations() {
	await nextTick()

	const section = document.querySelector('.handmade-masks')
	const wrapper = section?.querySelector('.handmade-masks__wrapper')
	const itemsEls = wrapper?.querySelectorAll('.handmade-masks__item')
	if (!section || !wrapper || !itemsEls) return

	const imgs = Array.from(section.querySelectorAll('img'))
	await Promise.all(
		imgs.map((img) =>
			img.complete
				? Promise.resolve()
				: new Promise((res) => {
						img.onload = img.onerror = res
					}),
		),
	)

	if (triggerInstance) {
		triggerInstance.kill()
		triggerInstance = null
	}

	if (window.innerWidth > 991.98) {
		const totalSteps = itemsEls.length
		const baseHeight = wrapper.offsetHeight
		const scrollLength = window.innerHeight * (totalSteps - 1) + baseHeight

		section.style.height = scrollLength + 'px'

		triggerInstance = ScrollTrigger.create({
			trigger: section,
			start: 'top+=5% top',
			end: '+=' + (scrollLength - baseHeight - 500),
			// pin: true,
			pin: wrapper,
			scrub: true,
			// markers: { startColor: 'red', endColor: 'green', fontSize: '16px', indent: 20 },
			onUpdate: (self) => {
				activeIndex.value = Math.min(totalSteps - 1, Math.floor(self.progress * totalSteps))
			},
		})
	} else {
		section.style.height = 'auto'
	}

	// Один refresh в самом конце
	// ScrollTrigger.refresh(true)
}

async function waitForStableLayout(maxChecks = 15, delay = 120) {
	let last = document.documentElement.scrollHeight
	for (let i = 0; i < maxChecks; i++) {
		await new Promise((r) => setTimeout(r, delay))
		const current = document.documentElement.scrollHeight
		if (current === last) return true
		last = current
	}
	return false
}
onMounted(async () => {
	await waitForStableLayout()
	await initAnimations()
	ScrollTrigger.refresh(true)
	resizeHandler = debounce(async () => {
		await initAnimations()
	}, 200)
	window.addEventListener('resize', resizeHandler)
})

onBeforeUnmount(() => {
	if (observer) observer.disconnect()
	if (triggerInstance) {
		triggerInstance.kill()
		triggerInstance = null
	}
	if (resizeHandler) window.removeEventListener('resize', resizeHandler)
})
</script>

<template>
	<section class="handmade-masks" id="manufactures">
		<div class="handmade-masks__bg-elem handmade-masks__bg-elem-1">
			<img
				loading="lazy"
				src="@/assets/img/handmade-masks/handmade-masks_star-bottom.svg"
				alt=""
			/>
		</div>
		<div class="handmade-masks__bg-elem handmade-masks__bg-elem-2">
			<img
				loading="lazy"
				src="@/assets/img/handmade-masks/handmade-masks_star-top-right.svg"
				alt=""
			/>
		</div>
		<div class="handmade-masks__container">
			<div class="handmade-masks__title">
				<h2>Каждая маска проходит <span>через наши руки</span></h2>
				<p>Это честное, локальное производство в Белгороде</p>
			</div>

			<div class="handmade-masks__wrapper">
				<div class="handmade-masks__list">
					<div v-for="(item, index) in items" :key="index" class="handmade-masks__item">
						<button
							class="handmade-masks__btn"
							:class="{ _active: activeIndex === index }"
							@click="setActive(index)"
						>
							{{ item.title }}
						</button>

						<div
							:class="{ _active: activeIndex === index }"
							class="handmade-masks__img"
						>
							<!-- <img :src="item.image" alt="" /> -->
							<video :src="item.image" autoplay muted loop playsinline></video>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<style lang="scss" scoped>
.handmade-masks {
	padding: 8.4rem 0 0;
	position: relative;

	@media (max-width: 991.98px) {
		height: auto;
		padding: 3.2rem 0;
	}

	// .handmade-masks__bg-elem
	&__bg-elem {
		position: absolute;
		width: 4.5rem;

		@media (max-width: 991.98px) {
			display: none;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
		}
	}

	// .handmade-masks__bg-elem-1
	&__bg-elem-1 {
		bottom: 0.5rem;
		left: 51.3%;
		transform: translateX(-50%);
	}

	// .handmade-masks__bg-elem-2
	&__bg-elem-2 {
		top: 26.5rem;
		right: 8rem;
	}

	// .handmade-masks__container
	&__container {
	}

	// .handmade-masks__title
	&__title {
		text-align: center;

		p {
			margin-top: 2.7rem;

			@media (max-width: 991.98px) {
				margin-top: 1rem;
			}
		}
	}

	// .handmade-masks__wrapper
	&__wrapper {
		position: relative;

		@media (max-width: 991.98px) {
			margin-top: 4.5rem;
		}
	}

	// .handmade-masks__desc
	&__desc {
	}

	// .handmade-masks__list
	&__list {
		counter-reset: li;

		@media (min-width: 991.98px) {
			padding: 17.9rem 0 0;
			max-width: 83.7rem;
			margin: 0px 0px 0px auto;
		}
	}

	// .handmade-masks__item
	&__item {
		&:nth-child(odd) {
			.handmade-masks__img {
				&::after {
					background-image: url('@/assets/img/handmade-masks/handmade-masks_bg-1.png');
				}

				img,
				video {
					mask-image: url('@/assets/img/handmade-masks/handmade-masks_mask-1.svg');
				}
			}
		}

		&:nth-child(even) {
			.handmade-masks__img {
				&::after {
					background-image: url('@/assets/img/handmade-masks/handmade-masks_bg-2.png');
				}

				img,
				video {
					mask-image: url('@/assets/img/handmade-masks/handmade-masks_mask-2.svg');
				}
			}
		}

		@media (max-width: 991.98px) {
			&:not(:last-child) {
				margin-bottom: 2.8rem;
			}
		}

		@media (min-width: 991.98px) {
			&:nth-child(1) {
				margin-bottom: 4rem;

				.handmade-masks__btn {
					transform: rotate(-12deg);
				}
			}

			&:nth-child(2) {
				margin-bottom: 5rem;

				.handmade-masks__btn {
					transform: rotate(-5deg);
					gap: 3.2rem;
				}
			}

			&:nth-child(3) {
				margin-bottom: 5.5rem;

				.handmade-masks__btn {
					gap: 2.9rem;
				}
			}

			&:nth-child(4) {
				.handmade-masks__btn {
					transform: rotate(5deg);
				}
			}
		}
	}

	// .handmade-masks__btn
	&__btn {
		cursor: pointer;
		font-weight: 400;
		font-size: 5rem;
		letter-spacing: 0.04em;
		text-transform: lowercase;
		text-align: left;
		display: flex;
		align-items: center;
		gap: 3.8rem;
		color: rgba(255, 255, 255, 0.34);
		transition: color 0.3s ease 0s;

		@media (max-width: 991.98px) {
			font-size: 2.2rem;
			// line-height: 2.6rem;
			line-height: 116%;
			color: #fff;
			width: 100%;
			justify-content: center;
			margin-bottom: 1.2rem;
			gap: 0.5rem;
			white-space: nowrap;

			&::before {
				color: #e2ff04;
			}
		}

		&::before {
			counter-increment: li;
			content: counter(li);
			transition: color 0.3s ease 0s;
		}

		@media (min-width: 991.98px) {
			&._active {
				color: #fff;

				&::before {
					color: #e2ff04;
				}
			}
		}
	}

	// .handmade-masks__img
	&__img {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 73.7rem;

		@media (min-width: 991.98px) {
			position: absolute;
			top: 3.2rem;
			left: 0;
			max-width: 87.9rem;
			opacity: 0;
			visibility: hidden;
			transition: all 0.3s ease 0s;

			&._active {
				opacity: 1;
				visibility: visible;
			}
		}

		@media (max-width: 991.98px) {
			height: 25.7rem;
		}

		&::after {
			content: '';
			position: absolute;
			width: 100%;
			height: 100%;
			left: 50%;
			top: 50%;
			transform: translate(-50%, -50%);
			background-repeat: no-repeat;
			background-position: center;
			background-size: contain;
		}

		img,
		video {
			mask-repeat: no-repeat;
			mask-position: center;
			mask-size: contain;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}
}

@media (min-width: 991.98px) {
	/* Плавное появление */
	.fade-enter-active,
	.fade-leave-active {
		transition:
			opacity 0.6s ease,
			transform 0.6s ease;
	}
	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
		transform: translateY(20px);
	}
	.fade-enter-to,
	.fade-leave-from {
		opacity: 1;
		transform: translateY(0);
	}
}
</style>
